import * as Yup from 'yup';

import {
  calculateEventAmounts,
  calculateEventPercents,
} from 'utils/event-allocation';
import type {
  StockTransferEventAllocation,
  StockTransferEventAllocationRequest,
} from 'store/api/stock-transfers/types';

import { EventSplitRow } from './types';

// T-L09 (owner rule L14, 2026-10-15): "Storage -> Storage: add an event
// selection + split like we have in expenses." Same rows, same rules and the
// same cent-exact helpers as the expense event split
// (utils/event-allocation.ts, shared with components/accounting-expenses-form).

export { calculateEventAmounts, calculateEventPercents };

export const EVENT_SPLIT_FIELD = 'events';

export const eventSplitRowInitialValues: EventSplitRow = {
  amount: undefined,
  eventId: undefined,
  percent: undefined,
};

const cents = (value: unknown) => Math.round(Number(value) * 100);

// StockTransferType.StorageToStorage (a literal here keeps this module free
// of the store's runtime imports).
export const EVENT_SPLIT_TRANSFER_TYPE = 'StorageToStorage';

export const isEventSplitType = (type?: string | null) =>
  type === EVENT_SPLIT_TRANSFER_TYPE;

export const getEventSplitInitialValues = (
  allocations?: StockTransferEventAllocation[] | null
): EventSplitRow[] =>
  (allocations || []).map((row) => ({
    amount: Number(row.amount),
    eventId: Number(row.eventId),
    percent: Number(row.percent),
  }));

/**
 * The split's validation, on top of the transfer form or the LTL dialog.
 * `priceField` names the LTL price field on the same form. No rows = no
 * split (valid; L10: no event).
 */
export const eventSplitSchema = (priceField = 'ltlPrice') =>
  Yup.array()
    .of(
      Yup.object().shape({
        eventId: Yup.number().required('Event is a required field'),
        percent: Yup.number()
          .typeError('Percent must be a number')
          .required('Percent is a required field')
          .min(0)
          .max(100),
        amount: Yup.number()
          .typeError('Amount must be a number')
          .required('Amount is a required field')
          .moreThan(0, 'Amount must be above 0'),
      })
    )
    .test(
      'price-first',
      'Enter the LTL price before splitting it over events',
      function (rows) {
        if (!rows?.length) {
          return true;
        }
        return cents(this.parent?.[priceField]) > 0;
      }
    )
    .test(
      'total-percentage',
      'The total percentage must be 100%',
      function (rows) {
        if (!rows?.length) {
          return true;
        }
        const total = rows.reduce(
          (sum, row) => sum + (Number(row?.percent) || 0),
          0
        );
        return Math.round(total * 100) === 10000;
      }
    )
    .test(
      'total-amount',
      'Event amounts must total the LTL price',
      function (rows) {
        if (
          !rows?.length ||
          rows.some(
            (row) => row?.amount == null || !Number.isFinite(Number(row.amount))
          )
        ) {
          return true;
        }
        return (
          rows.reduce((sum, row) => sum + cents(row.amount), 0) ===
          cents(this.parent?.[priceField])
        );
      }
    );

/** The request rows: dollars with 2 decimals, percent as typed. */
export const buildEventAllocations = (
  rows: EventSplitRow[] = []
): StockTransferEventAllocationRequest[] =>
  rows.map((row) => ({
    eventId: Number(row.eventId),
    percent: String(Number(row.percent)),
    amount: (cents(row.amount) / 100).toFixed(2),
  }));

export const sameEventSplit = (
  left: EventSplitRow[] = [],
  right: EventSplitRow[] = []
) =>
  JSON.stringify(buildEventAllocations(left)) ===
  JSON.stringify(buildEventAllocations(right));

/**
 * Create / edit transfer request body: `events` (the form rows) becomes
 * `eventAllocations` only on a Storage to Storage transfer, and only when
 * there is a split or one was saved before (so it can be cleared). Any other
 * body is exactly what it was before this field existed.
 */
export const withEventSplit = <
  T extends {
    type?: string;
    events?: EventSplitRow[];
    eventAllocations?: unknown;
  },
>(
  values: T,
  hadSplit = false
): T => {
  // the saved rows (read with the transfer) are never sent back as they are
  const rest = { ...values };
  delete rest.events;
  delete rest.eventAllocations;
  const { events } = values;
  if (isEventSplitType(values.type) && (events?.length || hadSplit)) {
    return {
      ...rest,
      eventAllocations: buildEventAllocations(events),
    } as unknown as T;
  }
  return rest as T;
};

/** "Show A, Show B" for lists and details; '' without a split. */
export const eventSplitNames = (
  allocations?: StockTransferEventAllocation[] | null
) =>
  (allocations || [])
    .map((row) => row.event?.name)
    .filter(Boolean)
    .join(', ');
